// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
/**
 * Sprint 65.18.0 — continuity plans and exercises (toolkit row IRP-01).
 *
 * ## What half of this row already exists, and is not rebuilt
 *
 * Incident playbook templates and activations, assignments, phases, evidence,
 * deadlines and post-incident review all ship. The gap the coverage review
 * names is the other half: *"No BCP/recovery-plan lifecycle or exercise
 * scheduler with participants, drill results, improvement actions, and
 * retest."*
 *
 * ## Why this control is elective, verified against the signed text
 *
 * `diễn tập`, `dự phòng` and continuity phrasing appear **zero times** in
 * either Law 91/2025/QH15 or Decree 356/2025/NĐ-CP, read from the signed
 * sources on 2026-09-21. The only adjacent text is a DPIA annex field assessing
 * *"khả năng phòng ngừa, phát hiện và ứng phó rủi ro, sự cố"* — an assessment
 * input, not a duty to run exercises.
 *
 * That measurement is over the **instruments**, not the pack. Sprint 65.16.1
 * established why the distinction matters: the pack encodes 10 of Law 91's 39
 * articles, and a term absent from it says nothing about the law.
 *
 * So this row's binding declares `instrument_imposes_no_duty` — the vocabulary
 * removed before 65.11.0 merged, whose docblock named **this row** as the one
 * it would fit and set the condition of its return: *"It comes back with its
 * first real declaration, not before."* This sprint is that declaration.
 *
 * ## The property the register exists to keep visible
 *
 * An exercise that produced improvement actions and no retest is not done. A
 * surface that showed it green would be worse than one that showed nothing,
 * because the loop it leaves open is the whole point of exercising.
 */
/** What the plan covers. Open text is deliberately absent — see `other`. */
export const CONTINUITY_PLAN_SCOPES = [
    'systems',
    'premises',
    'people',
    'suppliers',
    'data_recovery',
    'other',
];
/**
 * How the exercise was run.
 *
 * `tabletop` and `live` are not the same evidence and the difference is worth
 * keeping: one tests whether people know the plan, the other tests whether the
 * plan works.
 */
export const CONTINUITY_EXERCISE_KINDS = ['tabletop', 'simulation', 'live', 'other'];
/**
 * What the exercise showed.
 *
 * `not_completed` is a real outcome — an exercise abandoned halfway is
 * evidence about the plan, and folding it into "failed" would lose why.
 */
export const CONTINUITY_EXERCISE_OUTCOMES = [
    'objectives_met',
    'objectives_partially_met',
    'objectives_not_met',
    'not_completed',
];
/** The exercise's lifecycle. `state_field` for the adopted proof source. */
export const CONTINUITY_EXERCISE_STATUSES = ['scheduled', 'completed', 'cancelled'];
//# sourceMappingURL=continuity.js.map